import "bootstrap/dist/css/bootstrap.css";
import React from "react";
import { createRoot } from "react-dom/client";
import { BrowserRouter } from "react-router-dom";
import App from "./App";
import * as serviceWorkerRegistration from "./serviceWorkerRegistration";
import reportWebVitals from "./reportWebVitals";
import { PublicClientApplication, EventType } from "@azure/msal-browser";
import { getAppConfig } from "./services/ConfigService";
import { msalConfig } from "./authConfig.js";
import { AppConfigurationProvider } from "./contexts/AppConfigurationsContext.js";
import { MsalProvider } from "@azure/msal-react";
import { MsalAuthenticationTemplate } from "@azure/msal-react";
import { InteractionType } from "@azure/msal-browser";
import AuthorizationProvider from "./contexts/AuthorizationContext.js";

const appConfig = await getAppConfig();

/**
 * MSAL should be instantiated outside of the component tree to prevent it from being re-instantiated on re-renders.
 * For more, visit: https://github.com/AzureAD/microsoft-authentication-library-for-js/blob/dev/lib/msal-react/docs/getting-started.md
 */
const msalInstance = new PublicClientApplication({
  ...msalConfig,
  auth: {
    clientId: appConfig.REACT_APP_ENTRA_CLIENTID,
    authority: appConfig.REACT_APP_ENTRA_AUTHORITY,
    redirectUri: appConfig.REACT_APP_ENTRA_REDIRECTURI, // You must register this URI on Microsoft Entra admin center/App Registration. Defaults to window.location.origin
    postLogoutRedirectUri: appConfig.REACT_APP_ENTRA_POSTLOGOUTREDIRECTURI, // Indicates the page to navigate after logout.
  },
});

// Default to using the first account if no account is active on page load
if (
  !msalInstance.getActiveAccount() &&
  msalInstance.getAllAccounts().length > 0
) {
  // Account selection logic is app dependent. Adjust as needed for different use cases.
  msalInstance.setActiveAccount(msalInstance.getAllAccounts()[0]);
}

// Optional - This will update account state if a user signs in from another tab or window
msalInstance.enableAccountStorageEvents();

// Listen for sign-in event and set active account
msalInstance.addEventCallback((event) => {
  if (event.eventType === EventType.LOGIN_SUCCESS && event.payload.account) {
    const account = event.payload.account;
    msalInstance.setActiveAccount(account);
  }
});

const ErrorComponent = ({ error }) => {
  return (
    <div>
      There was an error authenticating the user. Pls try again later or contact
      support.
    </div>
  );
};

const LoadingComponent = () => {
  return (
    <div
      style={{
        display: "flex",
        fontSize: "32px",
        alignItems: "center",
        height: "100vh",
        width: "100vw",
        paddingLeft: "32px",
      }}
    >
      Authenticating user...
    </div>
  );
};

const rootElement = document.getElementById("root");
const root = createRoot(rootElement);
root.render(
  <BrowserRouter basename={appConfig.REACT_APP_BASENAME || ""}>
    <AppConfigurationProvider appConfig={appConfig}>
      <MsalProvider instance={msalInstance}>
        <MsalAuthenticationTemplate
          interactionType={InteractionType.Redirect}
          authenticationRequest={{
            scopes: [
              appConfig.REACT_APP_ENTRA_SCOPE,
              appConfig.REACT_APP_ENTRA_SCOPE,
            ],
          }}
          errorComponent={ErrorComponent}
          loadingComponent={LoadingComponent}
        >
          <AuthorizationProvider instance={msalInstance}>
            <App />
          </AuthorizationProvider>
        </MsalAuthenticationTemplate>
      </MsalProvider>
    </AppConfigurationProvider>
  </BrowserRouter>
);

// If you want your app to work offline and load faster, you can change
// unregister() to register() below. Note this comes with some pitfalls.
// Learn more about service workers: https://cra.link/PWA
serviceWorkerRegistration.unregister();

// If you want to start measuring performance in your app, pass a function
// to log results (for example: reportWebVitals(console.log))
// or send to an analytics endpoint. Learn more: https://bit.ly/CRA-vitals
reportWebVitals();
